import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PhoneEntry from "../components/PhoneEntry";
import StudentDetails from "../components/StudentDetails";
import MakePaymentForm from "../components/MakePaymentForm";
import { getStudentByMobile } from "../api/studentApi";
import { StudentByMobileResponse } from "../types";
import "../styles/App.css";

const MOBILE_STORAGE_KEY = "student-portal-mobile";

type PortalView = "details" | "payment";

const normalizeMobileParam = (value: string | null): string => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }
  return digits.length === 10 ? digits : "";
};

const PaymentFlow: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMobileHandled = useRef(false);

  const [mobileNumber, setMobileNumber] = useState<string>(() => {
    return sessionStorage.getItem(MOBILE_STORAGE_KEY) || "";
  });
  const [profile, setProfile] = useState<StudentByMobileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState<PortalView>("details");

  const loadProfile = async (
    number: string,
    options?: { openPayment?: boolean }
  ) => {
    try {
      setLoading(true);
      setError("");
      const response = await getStudentByMobile(number);
      if (!response.success || !response.data) {
        sessionStorage.removeItem(MOBILE_STORAGE_KEY);
        setMobileNumber("");
        setProfile(null);
        setError(
          response.message || "No student found with this mobile number."
        );
        return;
      }

      if (!response.data.student.isActive) {
        sessionStorage.removeItem(MOBILE_STORAGE_KEY);
        setMobileNumber("");
        setProfile(null);
        const name =
          response.data.student.fullName?.trim() ||
          `${response.data.student.firstName} ${response.data.student.lastName}`.trim();
        setError(
          `Sorry ${name}, your account is inactive. To proceed, please contact Parthao Lad on 9405703380.`
        );
        return;
      }

      sessionStorage.setItem(MOBILE_STORAGE_KEY, number);
      setMobileNumber(number);
      setProfile(response.data);
      setView(options?.openPayment ? "payment" : "details");
    } catch {
      sessionStorage.removeItem(MOBILE_STORAGE_KEY);
      setMobileNumber("");
      setProfile(null);
      setError("Unable to fetch student details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const mobileFromUrl = normalizeMobileParam(searchParams.get("mobile"));
    const openPayment =
      searchParams.get("pay") === "1" ||
      searchParams.get("action") === "pay";

    if (mobileFromUrl && !urlMobileHandled.current) {
      urlMobileHandled.current = true;
      setSearchParams({}, { replace: true });
      loadProfile(mobileFromUrl, { openPayment });
      return;
    }

    if (!mobileNumber || profile) {
      return;
    }
    loadProfile(mobileNumber);
  }, [mobileNumber, profile, searchParams, setSearchParams]);

  const handleStudentFound = (
    number: string,
    studentProfile: StudentByMobileResponse
  ) => {
    sessionStorage.setItem(MOBILE_STORAGE_KEY, number);
    setMobileNumber(number);
    setProfile(studentProfile);
    setView("details");
    setError("");
  };

  const handleChangeNumber = () => {
    sessionStorage.removeItem(MOBILE_STORAGE_KEY);
    setMobileNumber("");
    setProfile(null);
    setView("details");
    setError("");
  };

  const handlePaymentSuccess = async () => {
    if (!mobileNumber) {
      return;
    }
    setProfile(null);
    await loadProfile(mobileNumber);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading student details...</p>
      </div>
    );
  }

  if (!mobileNumber || !profile) {
    return (
      <>
        {error && <div className="global-error-banner">{error}</div>}
        <PhoneEntry onStudentFound={handleStudentFound} />
      </>
    );
  }

  if (view === "payment") {
    return (
      <MakePaymentForm
        student={profile.student}
        lastValidityTo={profile.lastPayment?.validityTo}
        onCancel={() => setView("details")}
        onSuccess={handlePaymentSuccess}
      />
    );
  }

  return (
    <StudentDetails
      profile={profile}
      onChangeNumber={handleChangeNumber}
      onMakePayment={() => setView("payment")}
    />
  );
};

export default PaymentFlow;
